/**
 * @file Application bootstrap (1.13.6, F2.4, F2.5, F1.9).
 *
 * Loads the runtime configuration first, attaches the client error reporter,
 * then mounts the providers and the router, or the maintenance notice when
 * configuration asks for it.
 *
 * Stylesheet order matters and is the same as the storefront's: the
 * self-hosted font faces, the Coven theme values, the tokens that map onto
 * them, the reset, then the global base styles. Those five files are copies of
 * the storefront's, unchanged, which is what makes this page look like the
 * same shop rather than merely similar to it.
 */
import './styles/fonts.css';
import './styles/themes/coven.css';
import './styles/tokens.css';
import './styles/reset.css';
import './styles/global.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { AppProviders } from './app/providers';
import { AppRouter } from './app/router';
import { loadRuntimeConfig } from './lib/config/loadConfig';
import { initErrorReporting } from './lib/observability/reporter';
import { Maintenance } from './routes/system/Maintenance';

/** Shown when config.json is missing or invalid: the deployment, not the visitor, is at fault. */
const BOOTSTRAP_FAILURE_MESSAGE =
  'This page is temporarily unavailable. Please try again in a few minutes.';

async function bootstrap(): Promise<void> {
  const rootElement = document.getElementById('root');
  if (rootElement === null) {
    throw new Error('index.html is missing the #root element');
  }
  try {
    const config = await loadRuntimeConfig();
    // Attached before the first render so an uncaught exception or a rejected
    // promise from the very first paint is still reported (F1.9). Off by
    // default: without `errorReportingEnabled` a report is only written to
    // the console. The page lives until navigation, so the detach function is
    // never needed here.
    initErrorReporting(config);
    const root = createRoot(rootElement);
    if (config.maintenanceMode) {
      root.render(
        <StrictMode>
          <Maintenance />
        </StrictMode>,
      );
      return;
    }
    root.render(
      <StrictMode>
        <AppProviders config={config}>
          <AppRouter />
        </AppProviders>
      </StrictMode>,
    );
  } catch (error) {
    rootElement.textContent = BOOTSTRAP_FAILURE_MESSAGE;
    // Surfaced for operators; the visitor sees only the message above.
    console.error('Bootstrap failed', error);
  }
}

void bootstrap();
